import React, { useState, useEffect } from 'react'
import HomeTemplate from '../templates/HomeTemplate'
import { HelpCircle, BookOpen, MessageCircle, Users, Shield, Zap, Globe, ArrowRight, Search, CheckCircle } from 'lucide-react'
import { Link } from 'react-router-dom'
import './HelpPage.css'

const HelpPage = () => {
  const [lastUpdated, setLastUpdated] = useState(null)

  useEffect(() => {
    setLastUpdated(
      new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    )
  }, [])

  useEffect(() => {
    document.title = 'Help Center - EveryKits | Support & Guidance'

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Get help using EveryKits tools. Find user guides, troubleshooting tips, and support for our free online calculators and converters.')
    }

    // Add structured data for help page
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "EveryKits Help Center",
      "description": "Comprehensive help and support for EveryKits free online tools",
      "url": "https://everykits.com/help",
      "mainEntity": {
        "@type": "FAQPage",
        "mainEntity": helpData.flatMap(category =>
          category.items.map(item => ({
            "@type": "Question",
            "name": item.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": item.answer
            }
          }))
        )
      }
    }

    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.textContent = JSON.stringify(structuredData)
    document.head.appendChild(script)

    return () => {
      document.head.removeChild(script)
    }
  }, [])

  const helpData = [
    {
      category: "Getting Started",
      icon: BookOpen,
      items: [
        {
          question: "How do I start using EveryKits tools?",
          answer: "Simply navigate to any tool page and start using it immediately. No registration or download required. All tools work directly in your browser with instant results."
        },
        {
          question: "Are EveryKits tools really free?",
          answer: "Yes, all our tools are completely free with no hidden charges, premium features, or subscription requirements. We believe in making digital tools accessible to everyone."
        },
        {
          question: "Do I need to create an account?",
          answer: "No account needed! You can use all our tools instantly without registration, email addresses, or sign-up processes. Just visit and use."
        }
      ]
    },
    {
      category: "Technical Issues",
      icon: Zap,
      items: [
        {
          question: "A tool isn't working, what should I do?",
          answer: "Try refreshing the page, clearing your browser cache, or using a different browser. If issues persist, contact us with details about the problem and your browser version."
        },
        {
          question: "Why is a tool running slowly?",
          answer: "Performance issues may be due to large file sizes, slow internet connection, or browser limitations. Try reducing file sizes or closing other browser tabs to improve performance."
        },
        {
          question: "Are there any browser requirements?",
          answer: "Our tools work best on modern browsers including Chrome, Firefox, Safari, and Edge. Ensure your browser is updated to the latest version for optimal performance."
        }
      ]
    },
    {
      category: "Privacy & Security",
      icon: Shield,
      items: [
        {
          question: "Is my data safe and private?",
          answer: "Yes, privacy is our priority. Most tools process data entirely in your browser. We don't store your files, track inputs, or collect personal information."
        },
        {
          question: "Do you store my files or data?",
          answer: "No, we don't store your files or data. All processing happens client-side in your browser. Your files never leave your device unless explicitly required for the tool's functionality."
        },
        {
          question: "What about cookies and tracking?",
          answer: "We use minimal essential cookies only for basic functionality. We don't use tracking cookies or sell your data to third parties."
        }
      ]
    },
    {
      category: "Mobile & Devices",
      icon: Globe,
      items: [
        {
          question: "Do tools work on mobile devices?",
          answer: "Yes! EveryKits is fully responsive and optimized for all devices - smartphones, tablets, and desktop computers. Tools work seamlessly across all screen sizes."
        },
        {
          question: "Can I use tools offline?",
          answer: "Currently, our tools require an internet connection as they're web-based. We're exploring offline capabilities for future updates."
        },
        {
          question: "Are there mobile apps available?",
          answer: "We don't have mobile apps yet, but our website works perfectly on mobile browsers. Add us to your home screen for quick access!"
        }
      ]
    }
  ]

  const quickLinks = [
    { title: "Age Calculator", description: "Calculate exact age with zodiac signs", icon: "Age", link: "/age/" },
    { title: "Word Counter", description: "Analyze text statistics", icon: "Word", link: "/word-counter/" },
    { title: "Image Compressor", description: "Reduce image file sizes", icon: "Image", link: "/image-compressor/" },
    { title: "Password Generator", description: "Create secure passwords", icon: "Password", link: "/password/" },
    { title: "QR Code Generator", description: "Generate QR codes", icon: "QR", link: "/qr-generator/" },
    { title: "JSON Formatter", description: "Format JSON data", icon: "JSON", link: "/json-formatter/" }
  ]

  return (
    <HomeTemplate>
      <div className="help-page">
        {/* Hero Section */}
        <section className="help-hero">
          <div className="hero-content">
            <HelpCircle className="hero-icon" />
            <h1>Help Center</h1>
            <p>Get the most out of EveryKits tools with our comprehensive guides, troubleshooting tips, and support resources.</p>
          </div>
        </section>

        {/* Quick Start Guide */}
        <section className="quick-start">
          <h2>Quick Start Guide</h2>
          <div className="start-steps">
            <div className="start-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Choose Your Tool</h3>
                <p>Browse our collection of 11 free online tools or use the search bar to find what you need.</p>
              </div>
            </div>
            <div className="start-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Input Your Data</h3>
                <p>Enter your text, upload files, or input data directly into the tool interface.</p>
              </div>
            </div>
            <div className="start-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Get Instant Results</h3>
                <p>Receive immediate results with options to download, copy, or share your output.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Popular Tools */}
        <section className="popular-tools">
          <h2>Popular Tools</h2>
          <div className="tools-grid">
            {quickLinks.map((tool, index) => (
              <Link key={index} to={tool.link} className="tool-card">
                <div className="tool-info">
                  <h3>{tool.title}</h3>
                  <p>{tool.description}</p>
                </div>
                <ArrowRight className="tool-arrow" />
              </Link>
            ))}
          </div>
        </section>

        {/* Help Categories */}
        <section className="help-categories">
          <h2>Frequently Asked Questions</h2>
          <div className="categories-grid">
            {helpData.map((category, index) => (
              <div key={index} className="category-section">
                <div className="category-header">
                  <category.icon className="category-icon" />
                  <h3>{category.category}</h3>
                </div>
                <div className="category-items">
                  {category.items.map((item, itemIndex) => (
                    <div key={itemIndex} className="help-item">
                      <h4>{item.question}</h4>
                      <p>{item.answer}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Troubleshooting */}
        <section className="troubleshooting">
          <h2>Common Issues & Solutions</h2>
          <div className="troubleshooting-grid">
            <div className="trouble-item">
              <div className="trouble-icon">!</div>
              <div className="trouble-content">
                <h3>Tool Not Loading</h3>
                <p>Refresh the page, check your internet connection, or try a different browser. Clear your browser cache if problems persist.</p>
              </div>
            </div>
            <div className="trouble-item">
              <div className="trouble-icon">!</div>
              <div className="trouble-content">
                <h3>File Upload Issues</h3>
                <p>Check file size limits (usually 10MB), ensure correct file format, and try a different file if the problem continues.</p>
              </div>
            </div>
            <div className="trouble-item">
              <div className="trouble-icon">!</div>
              <div className="trouble-content">
                <h3>Results Not Showing</h3>
                <p>Ensure all required fields are filled, check for error messages, and verify your input data format is correct.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Support */}
        <section className="contact-support">
          <div className="support-content">
            <MessageCircle className="support-icon" />
            <h2>Still Need Help?</h2>
            <p>Can't find what you're looking for? Our support team is here to help you get the most out of EveryKits tools.</p>
            <div className="support-buttons">
              <Link to="/contact/" className="support-btn primary">
                Contact Support
                <ArrowRight className="btn-arrow" />
              </Link>
              <Link to="/faq/" className="support-btn secondary">
                Browse FAQ
                <ArrowRight className="btn-arrow" />
              </Link>
            </div>
          </div>
        </section>

        {/* Community */}
        <section className="community">
          <h2>Join Our Community</h2>
          <div className="community-grid">
            <div className="community-item">
              <Users className="community-icon" />
              <h3>Share Feedback</h3>
              <p>Help us improve by sharing your experience and suggestions for new tools.</p>
            </div>
            <div className="community-item">
              <BookOpen className="community-icon" />
              <h3>Learn & Grow</h3>
              <p>Check out our blog for tips, tutorials, and digital productivity guides.</p>
            </div>
            <div className="community-item">
              <Shield className="community-icon" />
              <h3>Stay Secure</h3>
              <p>Learn about our privacy-first approach and how we protect your data.</p>
            </div>
          </div>
        </section>

        {/* Last Updated */}
        <div className="last-updated">
          <p suppressHydrationWarning>Last updated: {lastUpdated || 'Recently'}</p>        </div>
      </div>
    </HomeTemplate>
  )
}

export default HelpPage